"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[12px] font-mono uppercase tracking-widest text-foreground relative py-2 after:absolute after:bottom-0 after:left-1/2 after:h-[1px] after:w-0 after:-translate-x-1/2 after:bg-secondary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-lg md:hidden flex flex-col py-4 px-6 gap-2 animate-in slide-in-from-top-2 duration-200">
      {loading ? <div className="flex flex-col gap-4">
          {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
        </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-sm font-mono uppercase tracking-widest text-foreground py-3 border-b border-border/50 last:border-0" onClick={() => setIsOpen(false)} data-index={i}>
            {item.label}
          </Link>)}
      <div className="pt-4 mt-2 border-t border-border/50">
        <Button className="w-full font-mono uppercase tracking-widest text-[12px] rounded-md bg-primary text-primary-foreground hover:bg-primary/90" asChild onClick={() => setIsOpen(false)}>
          <Link to="/BeginYourJourneyPage">Book a Tour</Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264937" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b ${isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-background border-border"}`}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 flex items-center w-1/3">
          <Link to="/" className="flex items-center gap-2">
            <WvcLogo className="h-7 w-auto text-foreground" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex justify-center w-1/3">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center justify-end gap-4 flex-shrink-0 w-1/3">
          <Button variant="outline" className="hidden md:inline-flex font-mono uppercase tracking-widest text-[11px] border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors rounded-md" asChild>
            <Link to="/BeginYourJourneyPage">Book a Tour</Link>
          </Button>
          
          <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="16">
        <MobileMenuConsumer isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </section>;
}